import { createApi } from '@reduxjs/toolkit/query/react';
import { authBaseQuery } from '../../app/authBaseQuery';

/**
 * Real-backend "Investment Plans" catalog — v1.6.0 addition to the Cavan IMS
 * API. Browsing (list/detail) is open to any authenticated user, no extra
 * permission/KYC gate beyond being logged in. Create/update/delete require
 * the manage-investment-plans permission, held by both admin and
 * super-admin (unlike Admin Management, which is super-admin only) — pages
 * gate their own Create/Edit/Delete UI on that, this slice doesn't enforce
 * it client-side.
 *
 * Subscribing to a plan has no endpoint yet — this slice only covers the
 * catalog (CRUD), never investor participation.
 *
 * Money/percentage fields are typed as numbers in the list/detail schema,
 * but the docs' own create-response example returns them as strings
 * ("0.00") — normalizePlan() defensively Number()-coerces every numeric
 * field for display, since either shape can show up.
 */
function normalizePlan(raw) {
  if (!raw) return raw;
  const max = raw.maximum_amount;
  return {
    id: raw.id ?? raw.uuid,
    name: raw.name ?? '',
    description: raw.description ?? '',
    minimum_amount: Number(raw.minimum_amount ?? 0),
    maximum_amount: max === null || max === undefined ? null : Number(max),
    roi_percentage: Number(raw.roi_percentage ?? 0),
    duration_in_days: Number(raw.duration_in_days ?? 0),
    // v1.16.0 — payout structure, snapshotted onto each investment created
    // from this plan at creation time; editing a plan's frequency later
    // never touches investments already created from it.
    payout_frequency: raw.payout_frequency ?? 'monthly',
    payout_frequency_label: raw.payout_frequency_label ?? null,
    photo_url: raw.photo_url ?? null,
    created_at: raw.created_at ?? null,
    updated_at: raw.updated_at ?? null,
  };
}

// Builds the multipart body shared by create/update. `photo` is only
// appended when the caller actually picked a new file — omitting the field
// entirely (never sending an empty/null one) is what tells the update route
// "leave the existing photo alone" per the docs. `maximum_amount` is
// likewise only sent when provided, since it's optional on create.
export function buildPlanFormData(values) {
  const formData = new FormData();
  formData.append('name', values.name);
  formData.append('description', values.description);
  formData.append('minimum_amount', values.minimum_amount);
  if (values.maximum_amount !== undefined && values.maximum_amount !== null && values.maximum_amount !== '') {
    formData.append('maximum_amount', values.maximum_amount);
  }
  formData.append('roi_percentage', values.roi_percentage);
  formData.append('duration_in_days', values.duration_in_days);
  // Always sent (unlike maximum_amount/photo) — it carries a client-side
  // default of 'monthly' matching the server's own default, so there's no
  // "leave it unset" case to preserve here.
  formData.append('payout_frequency', values.payout_frequency || 'monthly');
  if (values.photo instanceof File) {
    formData.append('photo', values.photo);
  }
  return formData;
}

export const investmentPlansApi = createApi({
  reducerPath: 'investmentPlansApi',
  baseQuery: authBaseQuery(),
  tagTypes: ['InvestmentPlan'],
  endpoints: (builder) => ({
    // GET /investor/investment-plans — any authenticated user, no manage-
    // investment-plans check in front of it. Backend fix: this route was
    // originally documented as bare /investment-plans, then corrected to
    // add the /investor prefix that had been left off by mistake — the
    // admin CRUD routes below were never affected by that bug.
    listInvestmentPlans: builder.query({
      query: () => ({ url: '/investor/investment-plans', method: 'GET' }),
      transformResponse: (response) => (response?.plans ?? []).map(normalizePlan),
      providesTags: (result = []) => [
        ...result.map(({ id }) => ({ type: 'InvestmentPlan', id })),
        { type: 'InvestmentPlan', id: 'LIST' },
      ],
    }),

    // GET /investor/investment-plans/{uuid} — same /investor-prefix fix as
    // the list endpoint above. The docs don't spell out the wrapper key for
    // a single object the way they do for the list, so this defensively
    // unwraps either `{ plan: {...} }` or a bare plan object.
    getInvestmentPlan: builder.query({
      query: (uuid) => ({ url: `/investor/investment-plans/${uuid}`, method: 'GET' }),
      transformResponse: (response) => normalizePlan(response?.plan ?? response),
      providesTags: (result, error, uuid) => [{ type: 'InvestmentPlan', id: uuid }],
    }),

    // POST /admin/investment-plans — multipart/form-data (photo upload).
    createInvestmentPlan: builder.mutation({
      query: (formData) => ({ url: '/admin/investment-plans', method: 'POST', data: formData }),
      transformResponse: (response) => normalizePlan(response?.plan ?? response),
      invalidatesTags: [{ type: 'InvestmentPlan', id: 'LIST' }],
    }),

    // PUT /admin/investment-plans/{uuid} — multipart/form-data, per the
    // docs. Confirmed live: a real PUT with a multipart body arrives at
    // Laravel with every field empty (PHP only populates the parsed body
    // for POST, not PUT), so the request 422s claiming every required
    // field is missing even though the form sent them. Fixed with Laravel's
    // standard method-spoofing — send as POST with `_method: 'PUT'` in the
    // form data — which is the documented workaround for exactly this
    // multipart-on-PUT limitation and doesn't change what the server does
    // with the request.
    updateInvestmentPlan: builder.mutation({
      query: ({ uuid, formData }) => {
        formData.append('_method', 'PUT');
        return { url: `/admin/investment-plans/${uuid}`, method: 'POST', data: formData };
      },
      transformResponse: (response) => normalizePlan(response?.plan ?? response),
      invalidatesTags: (result, error, { uuid }) => [
        { type: 'InvestmentPlan', id: uuid },
        { type: 'InvestmentPlan', id: 'LIST' },
      ],
    }),

    // DELETE /admin/investment-plans/{uuid} — hard delete, removes the
    // plan's photo too. No soft-delete/restore per the docs.
    deleteInvestmentPlan: builder.mutation({
      query: (uuid) => ({ url: `/admin/investment-plans/${uuid}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'InvestmentPlan', id: 'LIST' }],
    }),
  }),
});

export const {
  useListInvestmentPlansQuery,
  useGetInvestmentPlanQuery,
  useLazyGetInvestmentPlanQuery,
  useCreateInvestmentPlanMutation,
  useUpdateInvestmentPlanMutation,
  useDeleteInvestmentPlanMutation,
} = investmentPlansApi;
